import { alpha, Grid2, Skeleton, Tab, Tabs, Typography, useTheme } from "@mui/material";
import Container from "../../../components/Container";
import { useEffect, useMemo, useState } from "react";
import api from "../../../utils/axios";
import { IEmployee, IOrder, IService, IUser } from "sierra";
import * as Colors from "@mui/material/colors";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../contexts/AppContext";
import DataGridSkeleton from "../../../components/Skeletons/DataGridSkeleton";
import { getOrderStatus } from "../../../utils/getOrderStatus";
import socket from "../../../utils/socket";

export default function SystemOrders() {
    const theme = useTheme()
    const navigate = useNavigate()
    const { lang, translate } = useApp()

    const [orders, setOrders] = useState<Required<IOrder>[]>()
    const [tab, setTab] = useState<'all' | 'unpaid' | 'unassigned' | 'cancelled' | 'completed' | 'awaiting_validation' | 'awaiting_correction' | 'awaiting_approval'>('all')

    useEffect(() => {
        fetchOrders()
        socket.on("orders_changed", fetchOrders);
        return () => {
            socket.off("orders_changed", fetchOrders);
        }
    }, [])

    const fetchOrders = () => {
        api.get('/orders', { params: { exclude_fields: 'service_form,order_details,branch' } }).then(({ data }) => setOrders(data)).catch(console.error)
    }

    const filteredOrders = useMemo(() => orders?.filter(o => {
        if (tab === 'all') return true;
        else if (tab === 'unpaid') return getOrderStatus(o) === 'Awaiting Payment'
        else if (tab === 'unassigned') return getOrderStatus(o) === 'Awaiting Assignment'
        else if (tab === 'cancelled') return getOrderStatus(o) === 'Cancelled'
        else if (tab === 'awaiting_validation') return getOrderStatus(o) === 'Awaiting Validation'
        else if (tab === 'awaiting_approval') return getOrderStatus(o) === 'Awaiting External Approval'
        else if (tab === 'awaiting_correction') return getOrderStatus(o) === 'Awaiting Correction'
        else if (tab === 'completed') return getOrderStatus(o) === 'Order Completed'
    }), [orders, tab])


    const columns = [{
        field: 'order_id',
        headerName: translate('Order #'),
        // width: 100
        flex: 0.5,
    }, {
        field: 'customer',
        headerName: translate('Customer'),
        minWidth: 150,
        flex: 1,
        valueGetter: (value: IUser) => {
            return value.name
        }
    }, {
        field: 'employee',
        headerName: translate('Assigned to'),
        minWidth: 150,
        flex: 1,
        valueGetter: (value: IEmployee) => {
            return value?.name || translate('Unassigned')
        }
    }, {
        field: 'order_status',
        headerName: translate('Status'),
        minWidth: 150,
        flex: 1,
        renderCell: ({ row }: { row: IOrder }) => row.is_cancelled ? translate('Cancelled') : !row.order_paid ? translate('Awaiting Payment') : translate(getOrderStatus(row))
    }, {
        field: 'service',
        headerName: translate('Service Ordered'),
        minWidth: 150,
        flex: 1,
        valueGetter: (value: IService) => {
            console.log(value)
            return value.service_name[lang]
        }
    },
    {
        field: 'order_amount_total',
        headerName: translate('Amount (SAR)'),
        minWidth: 100,
        flex: 0.8,
        valueGetter: (value: number) => {
            return (value / 100).toFixed(2)
        },
    },
    {
        field: 'creation_timestamp',
        headerName: translate('Date/Time'),
        minWidth: 150,
        flex: 1,
        valueGetter: (value: Date) => {
            return new Date(value).toLocaleString(lang === 'ar' ? 'ar-SD' : undefined)
        },
    },
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 200,
        //     headerAlign: 'center',
        //     renderCell: ({ row }) => {
        //         return (
        //             <Grid container gap={2} display={'flex'} justifyContent={'center'}>
        //                 <Grid item>
        //                     <Button color="secondary" startIcon={<Visibility />} onClick={() => navigate('manage', { state: { order: row } })}>
        //                         Manage Order
        //                     </Button>
        //                 </Grid>
        //             </Grid>
        //         );
        //     },
        // },
    ];

    const generateRowClass = (backgroundColor: string) => {
        return ({
            // fontSize: 16,
            backgroundColor,
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(backgroundColor, 0.2),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            color: '#000000',
            '&.Mui-selected': {
                backgroundColor: Colors.grey[400],
                '&:hover, &.Mui-hovered': {
                    backgroundColor: Colors.grey[300],
                    // Reset on touch devices, it doesn't add specificity
                    '@media (hover: none)': {
                        backgroundColor: alpha(
                            backgroundColor,
                            0.2 + theme.palette.action.selectedOpacity,
                        ),
                    },
                },
            },
        }
        )
    }

    return (
        <Grid2 container size={'grow'} spacing={2} flexDirection={'column'}>

            <Container container size={'auto'} padding={2} justifyContent={'space-between'}>
                <Grid2>
                    <Typography variant='h4' fontWeight={'bold'} color='primary'>
                        {translate('Orders')}
                    </Typography>
                </Grid2>
            </Container>

            <Container container padding={2} gap={1} width={'100%'} size='auto'>
                {!orders ? Array.from({ length: 3 }).map((_, i) => <Grid2 key={i} size='auto' gap={1} width={{ xs: 75, sm: 150 }}><Skeleton variant="rounded" width={'100%'} height={50} /></Grid2>) :
                    <Grid2>
                        <Tabs variant='scrollable' value={tab} onChange={(_, v) => setTab(v)}>
                            <Tab label={`${translate("All")} (${orders.length})`} value={'all'} />
                            <Tab label={`${translate("Unpaid")} (${orders.filter(o => getOrderStatus(o) === 'Awaiting Payment').length})`} value={'unpaid'} />
                            <Tab label={`${translate("Unassigned")} (${orders.filter(o => getOrderStatus(o) === 'Awaiting Assignment').length})`} value={'unassigned'} />
                            <Tab label={`${translate("Cancelled")} (${orders.filter(o => getOrderStatus(o) === 'Cancelled').length})`} value={'cancelled'} />
                            <Tab label={`${translate("Completed")} (${orders.filter(o => getOrderStatus(o) === 'Order Completed').length})`} value={'completed'} />
                            <Tab label={`${translate("Awaiting Validation")} (${orders.filter(o => getOrderStatus(o) === 'Awaiting Validation').length})`} value={'awaiting_validation'} />
                            <Tab label={`${translate("Awaiting Approval")} (${orders.filter(o => getOrderStatus(o) === 'Awaiting External Approval').length})`} value={'awaiting_approval'} />
                            <Tab label={`${translate("Awaiting Correction")} (${orders.filter(o => getOrderStatus(o) === 'Awaiting Correction').length})`} value={'awaiting_correction'} />
                        </Tabs>
                    </Grid2>}
            </Container>

            <Container container flexDirection={'column'} flexWrap={'nowrap'} overflow={'auto'} spacing={2} padding={2} sx={{ direction: 'ltr' }}>
                {/* <Grid2 container padding={2} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
                    {
                        !filteredOrders ? Array.from({ length: 3 }).map((a, i) => <Grid2 key={i}><OrderCardSkeleton /></Grid2>) :
                            filteredOrders.map((o, i) => <OrderCard key={i} order={o} />)
                    }
                </Grid2>
                <Grid2 size={'grow'} /> */}

                {
                    !filteredOrders ? <DataGridSkeleton /> :
                        filteredOrders.length == 0 ?
                            <Typography>No orders to show</Typography> :
                            <StyledDataGrid
                                rowSpacingType={'margin'}
                                onCellClick={({ row }: { row: IOrder }) => navigate('manage?order_id=' + row.order_id)}
                                rows={filteredOrders.map((c, i) => ({ ...c, id: i }))}
                                columns={columns}
                                disableRowSelectionOnClick
                                getRowClassName={({ row }: { row: IOrder }) => row.is_cancelled ? 'cancelled' : !row.order_paid ? 'unpaid' : row.order_status}
                                sx={{
                                    '& .MuiDataGrid-row.unpaid': generateRowClass(Colors.purple[100]),
                                    '& .MuiDataGrid-row.pending': generateRowClass(Colors.blue[100]),
                                    '& .MuiDataGrid-row.completed': generateRowClass(Colors.green[100]),
                                    '& .MuiDataGrid-row.awaiting': generateRowClass(Colors.orange[100]),
                                    '& .MuiDataGrid-row.cancelled': generateRowClass(Colors.red[200]),
                                    '& .MuiDataGrid-row.validating': generateRowClass(Colors.yellow[100]),
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer'
                                    }
                                }}
                            />

                }
            </Container>

        </Grid2>
    )
}